import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PageHeader from '@/components/dashboard/PageHeader';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, RefreshCw, Download } from 'lucide-react';
import { RebatesStatsBar } from '@/components/rebates/RebatesStatsBar';
import {
  RebatesByStoreCard,
  RebatesByJobCard,
} from '@/components/rebates/RebatesByStoreCard';
import RebatesTable from '@/components/rebates/RebatesTable';
import ProXtraCard from '@/components/rebates/ProXtraCard';
import {
  fetchRebatesData,
  flattenRebates,
  computeRebateStats,
  applyRebateFilter,
  rebatesToCsv,
  downloadCsv,
  listRebateBatches,
  loadStoreRebateRates,
  getProXtraConfig,
  computeProXtraProgress,
} from '@/lib/rebatesData';
import { toast } from 'sonner';

export default function RebateTrackerPage() {
  const { t } = useTranslation();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState('');
  const [storeFilter, setStoreFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [quarterFilter, setQuarterFilter] = useState('all');
  const [batchFilter, setBatchFilter] = useState('all');
  const [proXtra, setProXtra] = useState(null); // { enabled, threshold_usd, period }

  const load = useCallback(async ({ silent = false } = {}) => {
    if (silent) setRefreshing(true);
    else setLoading(true);
    setError(null);
    try {
      const [data] = await Promise.all([fetchRebatesData(), loadStoreRebateRates()]);
      setJobs(data);
      setProXtra(getProXtraConfig()); // cached off the same settings fetch
    } catch (e) {
      setError(e?.message || t('rebateTracker.loadError'));
    } finally {
      if (silent) setRefreshing(false);
      else setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const allRows = useMemo(() => flattenRebates(jobs), [jobs]);
  const stats = useMemo(() => computeRebateStats(allRows), [allRows]);
  const batches = useMemo(() => listRebateBatches(allRows), [allRows]);
  const proXtraProgress = useMemo(
    () =>
      proXtra?.enabled
        ? computeProXtraProgress(jobs, {
            threshold: proXtra.threshold_usd,
            period: proXtra.period,
          })
        : null,
    [jobs, proXtra]
  );
  const filteredRows = useMemo(
    () =>
      applyRebateFilter(allRows, {
        search,
        store: storeFilter,
        status: statusFilter,
        quarter: quarterFilter,
        batch: statusFilter === 'submitted' ? batchFilter : 'all',
      }),
    [allRows, search, storeFilter, statusFilter, quarterFilter, batchFilter]
  );

  const handleJobUpdated = (updatedJob) => {
    if (!updatedJob?.id) return;
    setJobs((cur) =>
      cur.map((j) => (j.id === updatedJob.id ? { ...j, ...updatedJob } : j))
    );
  };

  const handleExport = () => {
    const csv = rebatesToCsv(filteredRows);
    const stamp = new Date().toISOString().slice(0, 10);
    const scope =
      quarterFilter === 'all' ? 'all' : quarterFilter.replace(/\s+/g, '');
    downloadCsv(`kocore-rebates-${scope}-${stamp}.csv`, csv);
    toast.success(t('rebateTracker.exportedToast', { count: filteredRows.length }));
  };

  return (
    <div data-testid="rebate-tracker-page">
      <PageHeader
        eyebrow={t('rebateTracker.eyebrow')}
        title={t('rebateTracker.title')}
        subtitle={t('rebateTracker.subtitle')}
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              onClick={() => load({ silent: true })}
              disabled={loading || refreshing}
              className="border-[hsl(var(--primary))]/30 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10 uppercase tracking-[0.16em] text-xs"
              data-testid="rebates-refresh"
            >
              <RefreshCw size={14} className={`mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              {t('rebateTracker.refresh')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleExport}
              disabled={filteredRows.length === 0}
              className="border-[hsl(var(--primary))]/30 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10 uppercase tracking-[0.16em] text-xs"
              data-testid="rebates-export"
            >
              <Download size={14} className="mr-2" />
              {t('rebateTracker.exportCsv')}
            </Button>
          </div>
        }
      />

      {error ? (
        <Alert
          className="mb-5 border-[hsl(var(--destructive))]/40 bg-[hsl(var(--destructive))]/10 text-foreground"
          data-testid="rebates-error"
        >
          <AlertCircle className="h-4 w-4 text-[hsl(var(--destructive))]" />
          <AlertDescription className="text-sm">{error}</AlertDescription>
        </Alert>
      ) : null}

      <RebatesStatsBar stats={stats} />

      {proXtraProgress ? (
        <div className="mb-4">
          <ProXtraCard progress={proXtraProgress} />
        </div>
      ) : null}

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mb-4">
        <div>
          <RebatesByStoreCard stats={stats} onPick={setStoreFilter} />
        </div>
        <div className="xl:col-span-2">
          <RebatesByJobCard
            stats={stats}
            onPick={(jobId) => {
              const row = (stats.byJob || []).find((r) => r.job_id === jobId);
              if (row) setSearch(row.job_name);
            }}
          />
        </div>
      </div>

      <RebatesTable
        rows={filteredRows}
        loading={loading}
        jobs={jobs}
        storeFilter={storeFilter}
        onStoreFilter={setStoreFilter}
        statusFilter={statusFilter}
        onStatusFilter={setStatusFilter}
        quarterFilter={quarterFilter}
        onQuarterFilter={setQuarterFilter}
        search={search}
        onSearch={setSearch}
        onChanged={handleJobUpdated}
        batches={batches}
        batchFilter={batchFilter}
        onBatchFilter={setBatchFilter}
        onBatchSent={(batchId) => {
          // Show what just went out: Submitted tab, this batch.
          setStatusFilter('submitted');
          setBatchFilter(batchId);
        }}
      />
    </div>
  );
}
